import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Search, X } from "lucide-react";
import { getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { normalizeDigits } from "@/lib/digits";

const PINNED: CountryCode[] = [
  "TR", "SA", "AE", "KW", "QA", "BH", "OM", "EG", "JO", "IQ",
  "SY", "LB", "MA", "DZ", "TN", "LY", "SD", "YE", "PS",
];

const FLAG = (cc: string) =>
  cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

// English country names map (subset / fallback to ISO)
const COUNTRY_NAMES: Partial<Record<CountryCode, string>> = {
  TR: "Turkey", SA: "Saudi Arabia", AE: "United Arab Emirates", KW: "Kuwait",
  QA: "Qatar", BH: "Bahrain", OM: "Oman", EG: "Egypt", JO: "Jordan", IQ: "Iraq",
  SY: "Syria", LB: "Lebanon", MA: "Morocco", DZ: "Algeria", TN: "Tunisia",
  LY: "Libya", SD: "Sudan", YE: "Yemen", PS: "Palestine",
};

interface Props {
  // Stored value in canonical "00<countrycode><number>" form. May also be empty.
  value: string;
  onChange: (value: string) => void;
  defaultCountry?: CountryCode;
  required?: boolean;
  placeholder?: string;
  className?: string;
}

// Convert "00..." stored value to E.164 "+..."
const fromStored = (v: string): string => {
  const digits = normalizeDigits(v).replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.startsWith("00")) return "+" + digits.slice(2);
  return digits ? "+" + digits : "";
};

// Convert E.164 "+..." to stored "00..."
const toStored = (e164: string): string => {
  if (!e164) return "";
  return e164.startsWith("+") ? "00" + e164.slice(1) : e164;
};

export const PhoneInput = ({
  value, onChange, defaultCountry = "TR", required, placeholder, className,
}: Props) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "en").slice(0, 2);

  const initial = fromStored(value);
  const initialParsed = initial ? parsePhoneNumberFromString(initial) : null;

  const [country, setCountry] = useState<CountryCode>(initialParsed?.country ?? defaultCountry);
  const [national, setNational] = useState<string>(initialParsed?.nationalNumber ?? "");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const click = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", click);
    return () => document.removeEventListener("mousedown", click);
  }, []);

  // Emit normalised value upstream whenever country/national changes.
  useEffect(() => {
    if (!national.trim()) {
      onChange("");
      return;
    }
    const e164 = "+" + getCountryCallingCode(country) + national.replace(/\D/g, "");
    onChange(toStored(e164));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, national]);

  const onTypeNational = (raw: string) => {
    const cleaned = normalizeDigits(raw).replace(/[^\d]/g, "");
    setNational(cleaned);
  };

  const allCountries = useMemo<CountryCode[]>(() => {
    const others = (getCountries() as CountryCode[])
      .filter((c) => !PINNED.includes(c))
      .sort((a, b) => (COUNTRY_NAMES[a] ?? a).localeCompare(COUNTRY_NAMES[b] ?? b));
    return [...PINNED, ...others];
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allCountries;
    return allCountries.filter((c) => {
      const name = (COUNTRY_NAMES[c] ?? c).toLowerCase();
      return c.toLowerCase().includes(q) ||
        name.includes(q) ||
        getCountryCallingCode(c).includes(q.replace(/^\+/, ""));
    });
  }, [query, allCountries]);

  const callingCode = "+" + getCountryCallingCode(country);

  return (
    <div className={className}>
      <div className="flex items-stretch gap-2" dir="ltr">
        <div ref={wrap} className="relative">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="inline-flex h-full items-center gap-1 rounded-md border border-border bg-background px-2 py-2 text-sm focus:border-primary focus:outline-none"
            aria-label={t("field.phone.country")}
          >
            <span className="text-base leading-none">{FLAG(country)}</span>
            <span className="text-xs font-bold text-muted-foreground">{callingCode}</span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
          {open && (
            <div className="absolute z-50 mt-1 w-72 overflow-hidden rounded-md border border-border bg-popover shadow-premium">
              <div className="border-b border-border p-2">
                <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2">
                  <Search className="h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("field.phone.country")}
                    className="w-full bg-transparent py-2 text-sm focus:outline-none"
                  />
                  {query && (
                    <button type="button" onClick={() => setQuery("")} aria-label="clear">
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  )}
                </div>
              </div>
              <div className="max-h-60 overflow-y-auto overscroll-contain py-1 [-webkit-overflow-scrolling:touch]">
                {filtered.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCountry(c); setOpen(false); setQuery(""); }}
                    className={`flex w-full min-h-[44px] items-center justify-between gap-2 px-3 py-2 text-sm hover:bg-secondary touch-manipulation ${c === country ? "bg-primary/10 font-bold" : ""}`}
                  >
                    <span className="inline-flex items-center gap-2 truncate">
                      <span>{FLAG(c)}</span>
                      <span className="truncate">{COUNTRY_NAMES[c] ?? c}</span>
                    </span>
                    <span className="text-xs text-muted-foreground">+{getCountryCallingCode(c)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <input
          type="tel"
          inputMode="numeric"
          value={national}
          onChange={(e) => onTypeNational(e.target.value)}
          required={required}
          placeholder={placeholder ?? "5XX XXX XX XX"}
          className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />
      </div>
      {/* lang attr noop to acknowledge i18n usage */}
      <span className="hidden" aria-hidden lang={lang} />
    </div>
  );
};

// Validate the "00..." stored value against libphonenumber-js.
export const isValidStoredPhone = (stored: string): boolean => {
  const e164 = fromStored(stored);
  if (!e164) return false;
  try { return isValidPhoneNumber(e164); } catch { return false; }
};
